import React, {useState} from 'react';
import styled, {css} from 'styled-components';

import {useConfigState} from '../context';
import {useUserDataContext} from '@bootcamp/web/src/contexts/UserData';
import {Column, Table, Heading, Expand, Card, Title, SubTitle} from '../../../components/Performance/shared';

import ProgressBar from '@bootcamp/web/src/components/ProgressBar';
import {AlertIcon} from '@bootcamp/web/src/bootcamps/pages/Performance/Subject';
import {CellIcon, SubjectCell, TopicCell, iconMap} from '@bootcamp/web/src/bootcamps/components/Performance/shared';
import theme from '@bootcamp/shared/src/styles/theme';


const Container = styled(Column)`
  margin-bottom: ${({theme}) => theme.layouts.spacing.l};

  ${({theme}) => theme.mediaQueries.tablet} {
    margin-bottom: ${({theme}) => theme.layouts.spacing.m};
  }
`;
const StyledHeading = styled(Heading)`
  ${({theme}) => theme.mediaQueries.tablet} {
    padding: 0 ${({theme}) => theme.layouts.spacing.m};
  }
`;
const StyledCard = styled(Card)`
  margin-bottom: 0;
`;
const StyledTable = styled(Table)`

  td:nth-child(6) * {
    color: ${({theme}) => theme.colors.typographyPalette.secondary};
  }
  td:nth-child(6) {
    color: ${({theme}) => theme.colors.typographyPalette.secondary};
  }

  ${({bootcamp}) => ['dat', 'oat'].includes(bootcamp) && css`
    td:nth-child(5) {
      color: ${({theme}) => theme.colors.typographyPalette.secondary};
      white-space: nowrap;
    }
    td:nth-child(2) {
      width: auto;
    }

    tr:first-child {
      td:last-child {
        text-align: center;
      }
    }
  `}
`;
const ScoreRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;
const CorrectText = styled.span`
  width: 56px;
  white-space: nowrap;
`;
const StyledTopicCell = styled(TopicCell)`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
`;
const StyledProgressBar = styled(ProgressBar)`
  width: 100px;
`;
const BarCell = styled.span`
  width: 38px;
`;

const OuterWrapper = styled.div`
  ${({theme}) => theme.mediaQueries.tablet} {
    overflow-x: auto;
  }
  `;
const Wrapper = styled(Card)`
  margin-bottom: ${({theme}) => theme.layouts.spacing.l};

  ${({theme}) => theme.mediaQueries.tablet} {
    margin-bottom: ${({theme}) => theme.layouts.spacing.m};

    margin-right: ${({theme}) => theme.layouts.spacing.m};
    margin-left: ${({theme}) => theme.layouts.spacing.m};
    width: 800px;
  }
`;


const Subject = ({
  isFullLengthTest
}) => {
  const {scoring, config} = useConfigState();

  const singleSubject = Object.keys(scoring?.subjectBreakdown || {}).length === 1;

  const {bootcamp} = useUserDataContext();

  const [expanded, setExpanded] = useState(singleSubject);

  const rows = ['dat', 'oat'].includes(bootcamp)
    ? Object
    .entries(scoring?.subjectBreakdown || {})
    .map(([subject, breakdown]) => ({
      parent: [
        {
          content: (
            <SubjectCell>
              {iconMap?.[bootcamp]?.[subject] && <CellIcon src={iconMap?.[bootcamp]?.[subject]} />}
              {subject} {breakdown?.score < breakdown?.avgScore && <AlertIcon />}
            </SubjectCell>
          )
        },
        { content: `${breakdown?.score} / ${breakdown?.maxScore}` },
        {
          content: (
            <ScoreRow>
              <BarCell>{Math.round(breakdown?.score / breakdown?.maxScore * 100)}%</BarCell>
              <StyledProgressBar
                withMargin={false}
                showLabel={false}
                total={100}
                segments={[{ count: Math.round(breakdown?.score / breakdown?.maxScore * 100), color: theme.colors.brandPalette.blue.gradient }]}
                markPosition={Math.round((breakdown?.avgScore / breakdown?.maxScore) * 100)} />
            </ScoreRow>
          )
        },
        { content: `${Math.round((breakdown?.avgScore / breakdown?.maxScore) * 100)}%`, disabled: true },
      ],
      nested: Object
        .entries(breakdown?.systems)
        .sort(([a], [b]) => a?.localeCompare(b))
        .map(([system, breakdown]) => ({
          onClick: null,
          noHover: true,
          cells: [
            { content: '' },
            { content: (<StyledTopicCell>{system}{breakdown?.score < breakdown?.avgScore && <AlertIcon />}</StyledTopicCell>) },
            { content: `${breakdown?.score} / ${breakdown?.maxScore}` },
            {
              content: (
                <ScoreRow>
                  <BarCell>{Math.round(breakdown?.score / breakdown?.maxScore * 100)}%</BarCell>
                  <StyledProgressBar
                    withMargin={false}
                    showLabel={false}
                    total={100}
                    segments={[{ count: Math.round(breakdown?.score / breakdown?.maxScore * 100), color: theme.colors.brandPalette.blue.gradient }]}
                    markPosition={Math.round((breakdown?.avgScore / breakdown?.maxScore) * 100)} />
                </ScoreRow>
              )
            },
            { content: `${Math.round((breakdown?.avgScore / breakdown?.maxScore) * 100)}%`, disabled: true },
          ]
        }))
    }))
    : Object
      .entries(scoring?.subjectBreakdown || {})
      .map(([subject, breakdown]) => ({
        parent: [
          {content: (
            <SubjectCell>
              {iconMap?.[bootcamp]?.[subject] && <CellIcon src={iconMap?.[bootcamp]?.[subject]} />}
              {subject} {breakdown?.score < breakdown?.avgScore && <AlertIcon/>}
            </SubjectCell>
          )},
          {content: `${breakdown?.score} / ${breakdown?.maxScore}`},
          {content: (
            <ScoreRow>
              <span style={{width: 56}}>{Math.round(breakdown?.score / breakdown?.maxScore * 100)}%</span>
              <ProgressBar
                withMargin={false}
                showLabel={false}
                total={100}
                segments={[{count: Math.round(breakdown?.score / breakdown?.maxScore * 100), color: theme.colors.brandPalette.blue.gradient}]}
                markPosition={Math.round((breakdown?.avgScore / breakdown?.maxScore) * 100)}
              />
            </ScoreRow>
          )},
          {content: `${Math.round((breakdown?.avgScore / breakdown?.maxScore) * 100)}%`, disabled: true},
        ],
        nested: Object
          .entries(breakdown?.systems)
          .sort(([a], [b]) => a?.localeCompare(b))
          .map(([system, breakdown]) => ({
            onClick: null,
            noHover: true,
            cells: [
              {content: ''},
              // {content: subject, disabled: true},
              {content: (<span>{system}{subject === 'Simulation Exam' && breakdown?.score < breakdown?.avgScore && <AlertIcon />}</span>)},
              {content: `${breakdown?.score} / ${breakdown?.maxScore}`},
              {content: (
                <ScoreRow>
                  <span style={{width: 56}}>{Math.round(breakdown?.score / breakdown?.maxScore * 100)}%</span>
                  <ProgressBar
                    withMargin={false}
                    showLabel={false}
                    total={100}
                    segments={[{count: Math.round(breakdown?.score / breakdown?.maxScore * 100), color: theme.colors.brandPalette.blue.gradient}]}
                    markPosition={Math.round((breakdown?.avgScore / breakdown?.maxScore) * 100)}
                  />
                </ScoreRow>
              )},
              {content: `${Math.round((breakdown?.avgScore / breakdown?.maxScore) * 100)}%`, disabled: true},
            ]
          }))
      }));

  const defaultRows = [{content: bootcamp === 'nclex' ? 'Correct / Total Pts' : 'Correct / Total'}, {content: 'Score'}, {content: 'Avg. Score', disabled: true}, {content: ''}];

  const headerRows = ['dat', 'oat'].includes(bootcamp)
    ? [{content: ''}, {content: 'Subject'}, {content: 'Correct / Total'}, {content: 'Raw Score'}, {content: 'Avg. Raw Score', disabled: true}, {content: ''}]
    :  bootcamp === 'med-school' && config?.type !== 'anatomy'
    ? [{content: ''}, {content: 'System'}, ...defaultRows]
    : bootcamp === 'nclex'
    ? [{content: ''}, {content: 'Subject'}, ...defaultRows]
    : [{content: ''}, {content: 'Subject'}, ...defaultRows];


  const titleText = ['dat', 'oat'].includes(bootcamp) && !isFullLengthTest
    ? 'Topic'
    : headerRows[1].content;

  return (
    <Container>
      <StyledHeading>
        <Title>
          {titleText} Breakdown
          <Expand expanded={expanded} setExpanded={setExpanded} />
        </Title>
        <SubTitle>Increase your score by focusing on {!isFullLengthTest ? `${titleText?.toLowerCase()}s` : 'subjects'} marked with a red <AlertIcon/>- that's where you can improve the most!</SubTitle>
      </StyledHeading>
      <OuterWrapper>
        <Wrapper>
        <StyledCard>
          <StyledTable
            expandAll={expanded}
            hideChevrons
            bootcamp={bootcamp}
            // fixed={!['dat', 'oat'].includes(bootcamp)}
            fixed={false}
            rows={[
              {cells: headerRows},
              ...rows
            ]}
          />
        </StyledCard>
        </Wrapper>
      </OuterWrapper>
  </Container>
  );
};

export default Subject;